import { useMemo } from 'react';
import { useLocationStore } from '../store/useLocationStore';

/** A browse reference point: the coords every distance is measured FROM. */
export interface BrowseAnchor {
  lat: number;
  lon: number;
}

/**
 * The single reference point for every distance the app shows.
 *
 * Both browse surfaces — the home feed (`POST /listings/local/search`) and the
 * listing detail (`GET /listings/{id}`) — send THIS value as `lat`/`lon`, which
 * is what makes their distances agree. They used to disagree: the feed sent the
 * device fix while detail sent nothing at all, so the backend fell back to the
 * caller's saved profile address and measured from home. Same dish, same
 * `DishInfoCard`, wildly different miles for anyone away from their address.
 * The profile fallback is gone server-side; this hook is the client half, so
 * there is one anchor rather than one per surface.
 *
 * Resolution mirrors what the feed always did:
 *   - `locationSource === 'postal'`: the user typed a ZIP/city, so the
 *     operating location IS that locale's centroid — never a stale device fix.
 *   - otherwise: the user's own coords, falling back to the centroid so a
 *     locale-only user (mirrored from `profile.currentLocaleId`) still has an
 *     anchor and the search doesn't 400.
 *
 * This is the anchor, NOT the permission to display a distance. Whether a
 * number reaches the UI is a separate gate on `locationSource === 'device'`
 * (see the `stripDistance` select in `useListings`) — a centroid anchors the
 * feed without ever promising a distance.
 */
export function useBrowseAnchor(): BrowseAnchor | null {
  const locale = useLocationStore((s) => s.locale);
  const location = useLocationStore((s) => s.location);
  const locationSource = useLocationStore((s) => s.locationSource);

  const lat = locationSource === 'postal' ? locale?.lat : location?.lat ?? locale?.lat;
  const lon = locationSource === 'postal' ? locale?.lon : location?.lon ?? locale?.lon;

  return useMemo(
    () => (lat != null && lon != null ? { lat, lon } : null),
    [lat, lon],
  );
}

/**
 * Non-reactive read of the same anchor, for the imperative fetches that live
 * outside React (the WS toast path). Keeps those requests on the one anchor
 * rule — and on the same `['listing', id, anchor]` cache entry the detail page
 * uses, instead of forking a second one.
 */
export function getBrowseAnchor(): BrowseAnchor | null {
  const { locale, location, locationSource } = useLocationStore.getState();
  const lat = locationSource === 'postal' ? locale?.lat : location?.lat ?? locale?.lat;
  const lon = locationSource === 'postal' ? locale?.lon : location?.lon ?? locale?.lon;
  return lat != null && lon != null ? { lat, lon } : null;
}
